import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../Layout/Layout';
import DataTable from 'react-data-table-component';
import { Button } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import { APIUrl } from '../../Helper/APIUrl';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'code-block'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
  'image',
  'code-block'
];

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

const parseJSON = async (response) => {
  try {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response', error);
    return null;
  }
};

const slugify = (value) => {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const getPlainTextFromHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
};

const getImageSrc = (path) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${APIUrl.BaseUrl}${path}`;
};

function ManageBlogs() {
  const token = JSON.parse(localStorage.getItem('tcn'));
  const localStorageUserId = JSON.parse(localStorage.getItem('userid'));
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);

  const [isEdit, setIsEdit] = useState(false);
  const [blogId, setBlogId] = useState(0);

  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');

  const [cardImage, setCardImage] = useState('');
  const [cardImageAltText, setCardImageAltText] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [mainImageAltText, setMainImageAltText] = useState('');

  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [url, setUrl] = useState('');
  const [isUrlManuallyEdited, setIsUrlManuallyEdited] = useState(false);
  const [lastAutoGeneratedUrl, setLastAutoGeneratedUrl] = useState('');

  const [isActive, setIsActive] = useState(true);

  const [cardImageFile, setCardImageFile] = useState(null);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [cardImagePreview, setCardImagePreview] = useState('');
  const [mainImagePreview, setMainImagePreview] = useState('');

  const cardImageInputRef = useRef(null);
  const mainImageInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const urlInputRef = useRef(null);

  const { quill, quillRef } = useQuill({ modules: quillModules, formats: quillFormats });

  const authToken = useMemo(() => {
    return typeof token === 'string' ? token : token?.token || token?.Token || '';
  }, [token]);

  useEffect(() => {
    if (!quill) return;

    quill.on('text-change', () => {
      setDescription(quill.root.innerHTML);
    });
  }, [quill]);

  useEffect(() => {
    if (!quill) return;
    const html = description || '';
    if (quill.root.innerHTML !== html) {
      quill.root.innerHTML = html;
    }
  }, [description, quill]);

  useEffect(() => {
    getAllBlogs();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      setFilteredBlogs(blogs);
      return;
    }

    const result = blogs.filter((item) => {
      return (
        (item.Title || '').toLowerCase().includes(term) ||
        (item.URL || '').toLowerCase().includes(term) ||
        (item.MetaTitle || '').toLowerCase().includes(term)
      );
    });

    setFilteredBlogs(result);
  }, [blogs, search]);

  useEffect(() => {
    const autoSlug = slugify(title);

    // Keep URL synced with title until admin diverges from the generated slug.
    if (!isUrlManuallyEdited || !url || url === lastAutoGeneratedUrl) {
      setUrl(autoSlug);
    }

    setLastAutoGeneratedUrl(autoSlug);
  }, [title, isUrlManuallyEdited, url, lastAutoGeneratedUrl]);

  const getAllBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(APIUrl.Blogs + 'GetAllBlogs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({})
      });

      const result = await parseJSON(response);
      if (result?.data) {
        const sorted = [...result.data].sort((a, b) => (b.BlogId || 0) - (a.BlogId || 0));
        setBlogs(sorted);
        setFilteredBlogs(sorted);
      } else {
        setBlogs([]);
        setFilteredBlogs([]);
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Failed to load blogs.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValidType = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type);
    if (!isValidType) {
      enqueueSnackbar('Only JPG, JPEG, PNG, or WEBP files are allowed.', { variant: 'warning' });
      event.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      enqueueSnackbar('Image size should be less than or equal to 2 MB.', { variant: 'warning' });
      event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    if (type === 'card') {
      setCardImageFile(file);
      setCardImagePreview(objectUrl);
    } else {
      setMainImageFile(file);
      setMainImagePreview(objectUrl);
    }

    event.target.value = '';
  };

  const resetForm = () => {
    setIsEdit(false);
    setBlogId(0);
    setTitle('');
    setShortDescription('');
    setDescription('');
    setCardImage('');
    setCardImageAltText('');
    setMainImage('');
    setMainImageAltText('');
    setMetaTitle('');
    setMetaDescription('');
    setMetaKeywords('');
    setUrl('');
    setIsUrlManuallyEdited(false);
    setLastAutoGeneratedUrl('');
    setIsActive(true);
    setCardImageFile(null);
    setMainImageFile(null);
    setCardImagePreview('');
    setMainImagePreview('');
    if (cardImageInputRef.current) cardImageInputRef.current.value = '';
    if (mainImageInputRef.current) mainImageInputRef.current.value = '';
  };

  const isURLUnique = (candidateUrl) => {
    const candidate = (candidateUrl || '').trim().toLowerCase();
    if (!candidate) return false;

    return !blogs.some((item) => {
      const sameId = Number(item.BlogId || 0) === Number(blogId || 0);
      return !sameId && (item.URL || '').trim().toLowerCase() === candidate;
    });
  };

  const validateForm = () => {
    if (!authToken) {
      enqueueSnackbar('Please login first.', { variant: 'warning' });
      return false;
    }

    if (!title.trim()) {
      enqueueSnackbar('Title is required.', { variant: 'warning' });
      titleInputRef.current?.focus();
      return false;
    }

    if (!shortDescription.trim()) {
      enqueueSnackbar('Short Description is required.', { variant: 'warning' });
      return false;
    }

    if (!getPlainTextFromHtml(description)) {
      enqueueSnackbar('Description is required.', { variant: 'warning' });
      return false;
    }

    if (!cardImage && !cardImageFile) {
      enqueueSnackbar('Card Image is required.', { variant: 'warning' });
      return false;
    }

    if (!cardImageAltText.trim()) {
      enqueueSnackbar('Card Image Alt Text is required.', { variant: 'warning' });
      return false;
    }

    if (!mainImage && !mainImageFile) {
      enqueueSnackbar('Main Image is required.', { variant: 'warning' });
      return false;
    }

    if (!mainImageAltText.trim()) {
      enqueueSnackbar('Main Image Alt Text is required.', { variant: 'warning' });
      return false;
    }

    if (!metaTitle.trim()) {
      enqueueSnackbar('Meta Title is required.', { variant: 'warning' });
      return false;
    }

    if (!metaDescription.trim()) {
      enqueueSnackbar('Meta Description is required.', { variant: 'warning' });
      return false;
    }

    if (!metaKeywords.trim()) {
      enqueueSnackbar('Meta Keywords are required.', { variant: 'warning' });
      return false;
    }

    if (!url.trim()) {
      enqueueSnackbar('URL is required.', { variant: 'warning' });
      urlInputRef.current?.focus();
      return false;
    }

    const normalizedURL = slugify(url);
    if (!normalizedURL) {
      enqueueSnackbar('Please enter a valid URL.', { variant: 'warning' });
      urlInputRef.current?.focus();
      return false;
    }

    if (!isURLUnique(normalizedURL)) {
      enqueueSnackbar('URL must be unique. This URL already exists.', { variant: 'warning' });
      urlInputRef.current?.focus();
      return false;
    }

    setUrl(normalizedURL);
    return true;
  };

  const saveBlog = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        BlogId: Number(blogId) || 0,
        Title: title.trim(),
        ShortDescription: shortDescription.trim(),
        Description: description,
        CardImage: cardImage || '',
        CardImageAltText: cardImageAltText.trim(),
        MainImage: mainImage || '',
        MainImageAltText: mainImageAltText.trim(),
        MetaTitle: metaTitle.trim(),
        MetaDescription: metaDescription.trim(),
        MetaKeywords: metaKeywords.trim(),
        URL: slugify(url),
        Value: '',
        IsActive: isActive,
        CreatedBy: Number(localStorageUserId) || null,
        CreatedOn: null,
        ModifyBy: Number(localStorageUserId) || null,
        ModifiedOn: null
      };

      const formData = new FormData();
      formData.append('BlogsJson', JSON.stringify(payload));
      if (cardImageFile) {
        formData.append('CardImage', cardImageFile);
      }
      if (mainImageFile) {
        formData.append('MainImage', mainImageFile);
      }

      const endpoint = isEdit
        ? `${APIUrl.Blogs}UpdateBlogs/${blogId}`
        : `${APIUrl.Blogs}InsertBlogs`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: formData
      });

      const result = await parseJSON(response);

      if (response.ok) {
        enqueueSnackbar(isEdit ? 'Blog updated successfully.' : 'Blog created successfully.', { variant: 'success' });
        resetForm();
        getAllBlogs();
        return;
      }

      enqueueSnackbar(result?.Message || 'Unable to save blog.', { variant: 'error' });
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Error while saving blog.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const editBlog = (id) => {
    const selected = blogs.find((item) => Number(item.BlogId) === Number(id));
    if (!selected) return;

    setIsEdit(true);
    setBlogId(selected.BlogId || 0);
    setTitle(selected.Title || '');
    setShortDescription(selected.ShortDescription || '');
    setDescription(selected.Description || '');
    setCardImage(selected.CardImage || '');
    setCardImageAltText(selected.CardImageAltText || '');
    setMainImage(selected.MainImage || '');
    setMainImageAltText(selected.MainImageAltText || '');
    setMetaTitle(selected.MetaTitle || '');
    setMetaDescription(selected.MetaDescription || '');
    setMetaKeywords(selected.MetaKeywords || '');
    const selectedTitle = selected.Title || '';
    const selectedUrl = selected.URL || '';
    const selectedAutoUrl = slugify(selectedTitle);

    setUrl(selectedUrl);
    setLastAutoGeneratedUrl(selectedAutoUrl);
    setIsUrlManuallyEdited(selectedUrl !== selectedAutoUrl);
    setIsActive(Boolean(selected.IsActive));

    setCardImageFile(null);
    setMainImageFile(null);

    const cardPreview = selected.CardImage
      ? (selected.CardImage.startsWith('http') ? selected.CardImage : `${APIUrl.BaseUrl}${selected.CardImage}`)
      : '';

    const mainPreview = selected.MainImage
      ? (selected.MainImage.startsWith('http') ? selected.MainImage : `${APIUrl.BaseUrl}${selected.MainImage}`)
      : '';

    setCardImagePreview(cardPreview);
    setMainImagePreview(mainPreview);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activateDeactivateBlog = async (id, value) => {
    try {
      setLoading(true);
      const response = await fetch(`${APIUrl.Blogs}ActivateDeactivateBlogs/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ Value: value })
      });

      const result = await parseJSON(response);
      if (response.ok) {
        enqueueSnackbar(value === 'activate' ? 'Blog activated successfully.' : 'Blog deactivated successfully.', {
          variant: value === 'activate' ? 'success' : 'warning'
        });
        getAllBlogs();
        return;
      }

      enqueueSnackbar(result?.Message || 'Unable to update status.', { variant: 'error' });
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Error while updating status.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: <strong>SNo.</strong>,
      selector: (row, index) => index + 1,
      width: '80px'
    },
    // {
    //   name: <strong>Blog Id</strong>,
    //   selector: (row) => row.BlogId,
    //   width: '100px'
    // },
    {
      name: <strong>Title</strong>,
      selector: (row) => row.Title,
      sortable: true,
      grow: 2
    },
    {
      name: <strong>URL</strong>,
      selector: (row) => row.URL,
      sortable: true,
      grow: 2
    },
    {
      name: <strong>Card Image</strong>,
      cell: (row) => {
        const src = getImageSrc(row.CardImage);
        return src ? (
          <img
            src={src}
            alt={row.CardImageAltText || 'Card'}
            className="w-16 h-10 object-cover rounded border border-slate-200"
            onError={(e) => {
              e.currentTarget.src = 'https://dummyimage.com/160x100/e2e8f0/64748b&text=No+Image';
            }}
          />
        ) : (
          <span className="text-xs text-slate-500">No Image</span>
        );
      },
      width: '130px'
    },
    {
      name: <strong>Main Image</strong>,
      cell: (row) => {
        const src = getImageSrc(row.MainImage);
        return src ? (
          <img
            src={src}
            alt={row.MainImageAltText || 'Main'}
            className="w-16 h-10 object-cover rounded border border-slate-200"
            onError={(e) => {
              e.currentTarget.src = 'https://dummyimage.com/160x100/e2e8f0/64748b&text=No+Image';
            }}
          />
        ) : (
          <span className="text-xs text-slate-500">No Image</span>
        );
      },
      width: '130px'
    },
    {
      name: <strong>Status</strong>,
      cell: (row) =>
        row.IsActive ? (
          <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</label>
        ) : (
          <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Inactive</label>
        )
    },
    {
      name: <strong>Action</strong>,
      cell: (row) => (
        <div className="flex items-center gap-2">
          {!row.IsActive ? (
            <Button
              onClick={() => activateDeactivateBlog(row.BlogId, 'activate')}
              className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-3 py-2"
            >
              <i className="fa fa-toggle-off" aria-hidden="true"></i>
            </Button>
          ) : (
            <Button
              onClick={() => activateDeactivateBlog(row.BlogId, 'deactivate')}
              className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2"
            >
              <i className="fa fa-toggle-on" aria-hidden="true"></i>
            </Button>
          )}

          <Button
            onClick={() => editBlog(row.BlogId)}
            className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 bg-slate-50">
        <div className="mx-auto  space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-blue-700 px-6 py-5">
              <h1 className="text-2xl font-bold text-white">Blog Management</h1>
              <p className="text-indigo-100 text-sm mt-1">Create, update and publish blog pages from admin panel.</p>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                  <input
                    ref={titleInputRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    type="text"
                    placeholder="Enter blog title"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">URL (Auto from title, editable)</label>
                  <input
                    ref={urlInputRef}
                    value={url}
                    onChange={(e) => {
                      const nextUrl = e.target.value;
                      const autoSlug = slugify(title);

                      setUrl(nextUrl);
                      setIsUrlManuallyEdited(nextUrl !== autoSlug);
                    }}
                    onBlur={() => {
                      const normalized = slugify(url);
                      const autoSlug = slugify(title);

                      setUrl(normalized);
                      setIsUrlManuallyEdited(normalized !== autoSlug);
                    }}
                    type="text"
                    placeholder="blog-url"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Unique URL is validated before save.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Short Description</label>
                <textarea
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  rows={3}
                  placeholder="Enter short description"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description (HTML Editor)</label>
                <div className="rounded-lg border border-slate-300 overflow-hidden bg-white">
                  <div ref={quillRef} className="min-h-[220px]" />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Card Image</label>
                  <input
                    ref={cardImageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(e) => handleImageUpload(e, 'card')}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                  />
                  <div className="mt-3 h-44 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center overflow-hidden">
                    {cardImagePreview ? (
                      <img src={cardImagePreview} alt="Card Preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm text-slate-500">Card image preview</span>
                    )}
                  </div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-3">Card Image Alt Text</label>
                  <input
                    value={cardImageAltText}
                    onChange={(e) => setCardImageAltText(e.target.value)}
                    type="text"
                    placeholder="Describe card image"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Main Image</label>
                  <input
                    ref={mainImageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(e) => handleImageUpload(e, 'main')}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                  />
                  <div className="mt-3 h-44 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center overflow-hidden">
                    {mainImagePreview ? (
                      <img src={mainImagePreview} alt="Main Preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm text-slate-500">Main image preview</span>
                    )}
                  </div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 mt-3">Main Image Alt Text</label>
                  <input
                    value={mainImageAltText}
                    onChange={(e) => setMainImageAltText(e.target.value)}
                    type="text"
                    placeholder="Describe main image"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Meta Title</label>
                  <input
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    type="text"
                    placeholder="Enter meta title"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    Is Active
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Meta Description</label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  rows={4}
                  placeholder="Enter meta description"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Meta Keywords</label>
                <textarea
                  value={metaKeywords}
                  onChange={(e) => setMetaKeywords(e.target.value)}
                  rows={5}
                  placeholder="keyword1, keyword2, keyword3"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <button
                  type="button"
                  onClick={saveBlog}
                  disabled={loading}
                  className="px-6 py-3 rounded-lg bg-cyan-600 text-white font-medium hover:bg-cyan-700 disabled:opacity-60"
                >
                  {loading ? 'Please wait...' : isEdit ? 'Update Blog' : 'Save Blog'}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 rounded-lg bg-slate-200 text-slate-800 font-medium hover:bg-slate-300"
                >
                  Clear Form
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
              <h2 className="text-xl font-semibold text-slate-900">All Blogs</h2>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search by title / URL / meta title"
                className="w-full sm:w-96 rounded-lg border border-slate-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <DataTable
              columns={columns}
              data={filteredBlogs}
              pagination
              responsive
              striped
              highlightOnHover
              progressPending={loading}
              noDataComponent={<div className="py-4 text-slate-500">No blog data found.</div>}
            />
          </section>
        </div>
      </main>
    </Layout>
  );
}

export default ManageBlogs;
